import React, { useState, useEffect } from 'react';
import { User } from '../types';
import { auth, handleUserLogin, signInWithEmailAndPassword } from '../services/db';
import { Mail, AlertCircle, Clock, KeyRound, Cat as CatIcon } from 'lucide-react';

interface AuthProps {
  onLogin: (user: User) => void;
}

export const Auth: React.FC<AuthProps> = ({ onLogin }) => {
  const [error, setError] = useState('');
  const [errorDetails, setErrorDetails] = useState('');
  const [projectId, setProjectId] = useState('');
  const [pendingUser, setPendingUser] = useState<User | null>(null);
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Get current Project ID to help debugging
    // @ts-ignore - firebase types might be strict about options
    const pid = auth.app.options.projectId || 'Unbekannt';
    setProjectId(pid);
  }, []);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setErrorDetails('');
    setIsLoading(true);

    try {
        const result = await signInWithEmailAndPassword(auth, email, password);
        const { user, error } = await handleUserLogin(result.user);
        
        if (error === 'pending_approval') {
            setPendingUser(user);
        } else {
            onLogin(user);
        }
    } catch (e: any) {
        console.error(e);
        setError('Login fehlgeschlagen');
        if (e.code === 'auth/invalid-credential' || e.code === 'auth/wrong-password' || e.code === 'auth/user-not-found') {
            setErrorDetails('E-Mail oder Passwort falsch.');
        } else if (e.code === 'auth/too-many-requests') {
            setErrorDetails('Zu viele Versuche. Bitte warten Sie kurz.');
        } else {
            setErrorDetails(e.message);
        }
    } finally {
        setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-4 py-12">
      <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl animate-[fadeIn_0.5s_ease-out]">
        <div className="h-2 bg-gradient-to-r from-yellow-400 via-red-500 to-purple-600"></div>
        
        <div className="p-8">
          <div className="mb-6 text-center">
             {/* Mascot Icon */}
             <div className="flex justify-center mb-6">
                <div className="h-32 w-32 sm:h-40 sm:w-40 rounded-full bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center shadow-inner border-4 border-white drop-shadow-xl transition-transform hover:scale-105 duration-500">
                    <CatIcon className="h-16 w-16 text-purple-400 opacity-80" />
                </div>
             </div>
             
             <h1 className="text-3xl font-bold bg-gradient-to-r from-yellow-400 via-red-500 to-purple-600 bg-clip-text text-transparent">
              PurrfectManager
            </h1>
            <p className="text-gray-500 mt-2">Bienvenido al sistema de gestión del refugio de animales</p>
          </div>

          {error && (
            <div className="mb-6 rounded-lg bg-red-50 p-4 border border-red-200">
              <div className="flex items-center text-red-700 font-medium mb-1">
                <AlertCircle className="mr-2 h-5 w-5" />
                {error}
              </div>
              {errorDetails && (
                <div className="text-sm text-red-600 ml-7 mt-1 mb-3 break-words">
                  {errorDetails}
                </div>
              )}
            </div>
          )}

          {pendingUser ? (
             <div className="space-y-6 text-center animate-[fadeIn_0.5s_ease-out]">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-yellow-100">
                   <Clock className="h-8 w-8 text-yellow-600" />
                </div>
                <div>
                   <h3 className="text-xl font-bold text-gray-800">Registro exitoso</h3>
                   <p className="mt-2 text-gray-600">
                     Su cuenta <strong>{pendingUser.email}</strong> ha sido creada.
                   </p>
                   <div className="mt-4 rounded-lg bg-yellow-50 p-4 text-sm text-yellow-800 border border-yellow-200">
                     Por favor espere hasta que un administrador active su acceso.
                   </div>
                </div>
                <button 
                  onClick={() => setPendingUser(null)}
                  className="w-full rounded-lg bg-gray-100 py-3 font-semibold text-gray-700 hover:bg-gray-200"
                >
                  Atrás
                </button>
             </div>
          ) : (
            <div>
                <form onSubmit={handleEmailLogin} className="space-y-4 animate-[fadeIn_0.3s_ease-out]">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">E-Mail Adresse</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Mail className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                                    placeholder="admin@tierheim.de"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Passwort</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <KeyRound className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="password"
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                                    placeholder="••••••••"
                                />
                            </div>
                        </div>
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
                        >
                            {isLoading ? 'Lädt...' : 'Anmelden'}
                        </button>
                    </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};